// BranchProvider — konteks cabang aktif untuk seluruh app (RFC-007 §5, §6).
// Cabang aktif berasal dari sesi (login.branchId / token), server otoritatif.
// Provider ini hanya MENAMPILKAN konteks + menyediakan pemilih cabang bagi
// yang berhak; ia tak menetapkan branchId transaksi (itu dari token sesi).
//
// Tugas:
//  - resolusi branchId -> Branch (kode/nama) untuk indikator & prefix struk;
//  - daftar cabang yang boleh dipilih: SEMUA bila branchViewAll (Pemilik),
//    selain itu hanya cabang yang ditugaskan (myBranches);
//  - mengisi kembali branchCode ke sesi (dipakai nomor struk berprefix).
//
// TIDAK memuat modal/cost — hanya identitas cabang (NFR-SEC-3).

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { listBranches, myBranches, type Branch } from "../api";
import { useAuth } from "./auth";

type BranchValue = {
  /** Cabang aktif sesi (objek lengkap) — null bila belum termuat / single-cabang. */
  active: Branch | null;
  /** ID cabang aktif (dari token sesi). */
  activeId: string;
  /** Kode cabang aktif (prefix struk), mis. "TGR-01". */
  activeCode: string;
  /** Cabang yang BOLEH dipilih user ini (semua bila viewAll, else myBranches). */
  options: Branch[];
  /** Semua cabang sistem (untuk pengelolaan/transfer; subset = options bila tak viewAll). */
  all: Branch[];
  /** True bila lebih dari satu cabang bisa dipilih (tampilkan pemilih). */
  canSwitch: boolean;
  /** True bila sesi boleh melihat lintas cabang (Pemilik) — pemilih cakupan laporan. */
  viewAll: boolean;
  loading: boolean;
  reload: () => void;
  /** Ganti cabang aktif (delegasi ke auth.switchBranch + reload daftar). */
  switchTo: (branchId: string) => Promise<void>;
};

const BranchContext = createContext<BranchValue | null>(null);

export function BranchProvider({ children }: { children: ReactNode }) {
  const { token, status, branchId, branchViewAll, setBranchCode, switchBranch } = useAuth();
  const [all, setAll] = useState<Branch[]>([]);
  const [mine, setMine] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(() => {
    if (!token) {
      setAll([]);
      setMine([]);
      return;
    }
    setLoading(true);
    // Daftar cabang dibutuhkan semua user terautentikasi (resolusi kode untuk
    // struk). myBranches hanya relevan bila tak viewAll (batasi pemilih).
    listBranches(token)
      .then((res) => setAll(res.branches))
      .catch(() => setAll([]))
      .finally(() => setLoading(false));
    if (!branchViewAll) {
      myBranches(token)
        .then((res) => setMine(res.branches.map((b) => b.branchId)))
        .catch(() => setMine([]));
    } else {
      setMine([]);
    }
  }, [token, branchViewAll]);

  useEffect(() => {
    if (status !== "authenticated") return;
    reload();
  }, [status, reload]);

  const active = useMemo(() => all.find((b) => b.id === branchId) ?? null, [all, branchId]);

  // Isi kode cabang ke sesi (dipakai nomor struk berprefix) begitu termuat.
  useEffect(() => {
    if (active?.code) setBranchCode(active.code);
  }, [active?.code, setBranchCode]);

  // Opsi pemilih: Pemilik (viewAll) lihat semua cabang aktif; selain itu hanya
  // cabang yang ditugaskan (UserBranch). Selalu sertakan cabang aktif agar
  // pemilih menampilkannya walau daftar mine belum termuat.
  const options = useMemo(() => {
    const usable = all.filter((b) => b.active !== false);
    if (branchViewAll) return usable;
    return usable.filter((b) => mine.includes(b.id) || b.id === branchId);
  }, [all, branchViewAll, mine, branchId]);

  const switchTo = useCallback(
    async (next: string) => {
      if (!next || next === branchId) return;
      await switchBranch(next);
      reload();
    },
    [branchId, switchBranch, reload],
  );

  const value = useMemo<BranchValue>(
    () => ({
      active,
      activeId: branchId,
      activeCode: active?.code ?? "",
      options,
      all,
      canSwitch: options.length > 1,
      viewAll: branchViewAll,
      loading,
      reload,
      switchTo,
    }),
    [active, branchId, options, all, branchViewAll, loading, reload, switchTo],
  );

  return <BranchContext.Provider value={value}>{children}</BranchContext.Provider>;
}

export function useBranch(): BranchValue {
  const ctx = useContext(BranchContext);
  if (!ctx) throw new Error("useBranch() harus dipakai di dalam <BranchProvider>");
  return ctx;
}
